import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { EnumDTO, EnumSearchReqDTO, EnumSearchResDTO } from 'src/models/enum';
import { userManagementAPIRoutes } from '..';

// Define a service using a base URL and expected endpoints
/**
 * enumServices is a collection of API endpoints for user management.
 */
export const enumServices = createApi({
  reducerPath: 'enumServices',
  tagTypes: ['UserTag', 'VendorTag'],
  baseQuery: fetchBaseQuery({
    baseUrl: userManagementAPIRoutes.baseUrl,

    prepareHeaders: (headers) => {
      // If we have a token set in local storage, let's assume that we should be passing it.
      const token = localStorage.getItem('jwtToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    /**
     * getEnumData is an endpoint for creating a new user.
     */
    getEnumData: builder.mutation({
      query: () => ({
        url: `${userManagementAPIRoutes.enums.endpoint}`,
        method: 'POST',
        body: {
          enumTypes: [
            'PACKAGE_TYPE',
            'ROOM_SHARING',
            'INCLUSION_TYPE',
            'EXCLUSION_TYPE',
            'AIRLINES_TYPE',
            'BUS_TYPE',
            'BOOKING_STATUS',
            'BANNER_POSITION',
            'VENDOR_TYPE',
            'LEAD_STATUS'
          ]
        }
      }),
      invalidatesTags: ['UserTag', 'VendorTag']
    }),

    saveEnumData: builder.mutation<EnumDTO, EnumDTO>({
      query: (body) => ({
        url: `${userManagementAPIRoutes.enums.endpoint}/${userManagementAPIRoutes.enums.saveEnumData}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['UserTag', 'VendorTag']
    }),

    updateEnumData: builder.mutation<
      EnumDTO,
      {
        body: EnumDTO;
        id: number;
      }
    >({
      query: ({ body, id }) => ({
        url: `${userManagementAPIRoutes.enums.endpoint}/${userManagementAPIRoutes.enums.updateEnumData}/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['UserTag', 'VendorTag']
    }),

    getEnumDataBySearchCriteria: builder.query<
      EnumSearchResDTO,
      EnumSearchReqDTO
    >({
      query: (body) => ({
        url: `${userManagementAPIRoutes.enums.endpoint}/${userManagementAPIRoutes.enums.getEnumDataBySearchCriteria}`,
        method: 'POST',
        body
      }),
      providesTags: ['UserTag', 'VendorTag']
    }),

    getEnumDataById: builder.mutation<EnumDTO, number>({
      query: (id) => ({
        url: `${userManagementAPIRoutes.enums.endpoint}/${userManagementAPIRoutes.enums.getEnumDataById}/${id}`,
        method: 'GET'
      })
    }),

    deleteEnumDataById: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `${userManagementAPIRoutes.enums.endpoint}/${userManagementAPIRoutes.enums.deleteEnumDataById}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['UserTag', 'VendorTag']
    })
  })
});

export const {
  useGetEnumDataMutation,
  useSaveEnumDataMutation,
  useUpdateEnumDataMutation,
  useGetEnumDataByIdMutation,
  useGetEnumDataBySearchCriteriaQuery,
  useDeleteEnumDataByIdMutation
} = enumServices;
